import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function FleetPDataView() {
    const API_URL = process.env.REACT_APP_API_URL;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [activeTab, setActiveTab] = useState('attendance');
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchData();
    }, [selectedDate]);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/data?date=${selectedDate}`
            );
            setData(response.data);
        } catch (err) {
            console.error('Error fetching FleetP data:', err);
            if (err.response?.status === 404) {
                setData(null);
            } else {
                setError(err.response?.data?.message || 'Failed to fetch data');
            }
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const summary = data?.dailySummary || {};
    const attendanceRecords = data?.attendanceRecords || [];
    const kmReadings = data?.kmReadings || [];
    const hasData = attendanceRecords.length > 0 || kmReadings.length > 0;

    const getStatusBadge = (status) => {
        const colors = {
            CHECKED_IN: { bg: '#dbeafe', text: '#1d4ed8' },
            CHECKED_OUT: { bg: '#dcfce7', text: '#166534' },
            ABSENT: { bg: '#fee2e2', text: '#991b1b' },
        };
        const c = colors[status] || { bg: '#f3f4f6', text: '#374151' };
        return (
            <span style={{
                padding: '3px 8px', borderRadius: '10px', fontSize: '11px',
                fontWeight: '500', backgroundColor: c.bg, color: c.text
            }}>
                {status || '-'}
            </span>
        );
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.headerRow}>
                <div>
                    <h2 style={styles.title}>
                        <i className="fa fa-database" style={{ marginRight: '10px' }}></i>
                        FleetP Data Dashboard
                    </h2>
                    <a href="/orders/fleetp-connect" style={styles.backLink}>
                        <i className="fa fa-arrow-left" style={{ marginRight: '6px' }}></i>
                        Back to Connection
                    </a>
                </div>
                <div style={styles.headerActions}>
                    {data?.connected && (
                        <div style={styles.liveIndicator}>
                            <div style={styles.liveDot}></div>
                            <span style={styles.liveText}>CONNECTED</span>
                        </div>
                    )}
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={styles.datePicker}
                    />
                    <button onClick={fetchData} style={styles.refreshBtn} disabled={loading}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>
            </div>

            {error && (
                <div style={styles.errorAlert}>
                    <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                    {error}
                </div>
            )}

            {loading ? (
                <div style={styles.loadingContainer}>
                    <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
                    Loading data...
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div style={styles.summaryGrid}>
                        <div style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{summary.totalAttendance || 0}</div>
                            <div style={styles.summaryLabel}>Attendance</div>
                            <i className="fa fa-calendar-check-o" style={styles.summaryIcon}></i>
                        </div>
                        <div style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{summary.totalKmEntries || 0}</div>
                            <div style={styles.summaryLabel}>KM Entries</div>
                            <i className="fa fa-road" style={styles.summaryIcon}></i>
                        </div>
                        <div style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{summary.totalDrivers || 0}</div>
                            <div style={styles.summaryLabel}>Drivers</div>
                            <i className="fa fa-users" style={styles.summaryIcon}></i>
                        </div>
                        <div style={styles.summaryCard}>
                            <div style={styles.summaryNumber}>{summary.recordsSent || 0}</div>
                            <div style={styles.summaryLabel}>Records Sent</div>
                            <i className="fa fa-paper-plane" style={styles.summaryIcon}></i>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div style={styles.tabBar}>
                        {['attendance', 'km', 'daily'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    ...styles.tabButton,
                                    ...(activeTab === tab ? styles.tabButtonActive : {})
                                }}
                            >
                                <i className={`fa fa-${tab === 'attendance' ? 'calendar-check-o' : tab === 'km' ? 'road' : 'list-alt'}`} style={{ marginRight: '6px' }}></i>
                                {tab === 'attendance' ? 'Attendance' : tab === 'km' ? 'KM Readings' : 'Daily Records'}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div style={styles.card}>
                        {!hasData ? (
                            <div style={styles.emptyState}>
                                <i className="fa fa-inbox" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
                                <h3 style={{ fontSize: '18px', color: '#666', marginBottom: '8px' }}>No data sent to FleetP today</h3>
                                <p style={{ fontSize: '14px', color: '#999' }}>
                                    Data will appear here once drivers check in and attendance/KM data is synced for {selectedDate}
                                </p>
                            </div>
                        ) : activeTab === 'attendance' ? (
                            <div>
                                <h4 style={styles.tabTitle}>Attendance Records ({attendanceRecords.length})</h4>
                                <div style={styles.tableContainer}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Driver</th>
                                                <th style={styles.th}>Check In</th>
                                                <th style={styles.th}>Check Out</th>
                                                <th style={styles.th}>Vehicle</th>
                                                <th style={styles.th}>KM</th>
                                                <th style={styles.th}>Status</th>
                                                <th style={styles.th}>Synced At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attendanceRecords.map((record, idx) => (
                                                <tr key={idx} style={idx % 2 === 0 ? styles.trEven : {}}>
                                                    <td style={styles.td}>
                                                        <div style={{ fontWeight: '500' }}>{record.driver?.driverName || '-'}</div>
                                                        <div style={{ fontSize: '11px', color: '#999' }}>{record.driver?.phoneNumber || ''}</div>
                                                    </td>
                                                    <td style={styles.td}>{formatTime(record.checkInTime)}</td>
                                                    <td style={styles.td}>{formatTime(record.checkOutTime)}</td>
                                                    <td style={styles.td}>{record.vehiclePlateNumber || '-'}</td>
                                                    <td style={styles.td}>{record.kmReading != null ? record.kmReading.toFixed(1) : '-'}</td>
                                                    <td style={styles.td}>{getStatusBadge(record.status)}</td>
                                                    <td style={styles.td}>{formatTime(record.syncedAt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : activeTab === 'km' ? (
                            <div>
                                <h4 style={styles.tabTitle}>KM Readings ({kmReadings.length})</h4>
                                {kmReadings.length === 0 ? (
                                    <div style={styles.emptyState}>
                                        <i className="fa fa-road" style={{ fontSize: '36px', color: '#ccc', marginBottom: '12px' }}></i>
                                        <p style={{ color: '#999', fontSize: '14px' }}>No KM readings for this date</p>
                                    </div>
                                ) : (
                                    <div style={styles.tableContainer}>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.th}>Driver</th>
                                                    <th style={styles.th}>Vehicle</th>
                                                    <th style={styles.th}>KM Reading</th>
                                                    <th style={styles.th}>Synced At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {kmReadings.map((record, idx) => (
                                                    <tr key={idx} style={idx % 2 === 0 ? styles.trEven : {}}>
                                                        <td style={styles.td}>
                                                            <div style={{ fontWeight: '500' }}>{record.driver?.driverName || '-'}</div>
                                                            <div style={{ fontSize: '11px', color: '#999' }}>{record.driver?.phoneNumber || ''}</div>
                                                        </td>
                                                        <td style={styles.td}>{record.vehiclePlateNumber || '-'}</td>
                                                        <td style={styles.td}>
                                                            <span style={{ fontWeight: '600', color: '#1976d2' }}>
                                                                {record.kmReading != null ? record.kmReading.toFixed(1) : '-'}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#999', marginLeft: '4px' }}>km</span>
                                                        </td>
                                                        <td style={styles.td}>{formatTime(record.syncedAt)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Daily Records Tab - Combined Summary */
                            <div>
                                <h4 style={styles.tabTitle}>Daily Summary - {selectedDate}</h4>

                                <div style={styles.dailyGrid}>
                                    <div style={styles.dailyItem}>
                                        <div style={styles.dailyItemLabel}>Total Attendance Records</div>
                                        <div style={styles.dailyItemValue}>{summary.totalAttendance || 0}</div>
                                    </div>
                                    <div style={styles.dailyItem}>
                                        <div style={styles.dailyItemLabel}>Total KM Entries</div>
                                        <div style={styles.dailyItemValue}>{summary.totalKmEntries || 0}</div>
                                    </div>
                                    <div style={styles.dailyItem}>
                                        <div style={styles.dailyItemLabel}>Total Drivers Active</div>
                                        <div style={styles.dailyItemValue}>{summary.totalDrivers || 0}</div>
                                    </div>
                                    <div style={styles.dailyItem}>
                                        <div style={styles.dailyItemLabel}>Total Records Sent to FleetP</div>
                                        <div style={styles.dailyItemValue}>{summary.recordsSent || 0}</div>
                                    </div>
                                </div>

                                {/* Combined records list */}
                                {hasData && (
                                    <div style={{ marginTop: '20px' }}>
                                        <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '12px' }}>
                                            All Records ({attendanceRecords.length + kmReadings.length})
                                        </h5>
                                        <div style={styles.tableContainer}>
                                            <table style={styles.table}>
                                                <thead>
                                                    <tr>
                                                        <th style={styles.th}>Type</th>
                                                        <th style={styles.th}>Driver</th>
                                                        <th style={styles.th}>Vehicle</th>
                                                        <th style={styles.th}>Details</th>
                                                        <th style={styles.th}>Synced At</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {attendanceRecords.map((r, idx) => (
                                                        <tr key={`a-${idx}`} style={idx % 2 === 0 ? styles.trEven : {}}>
                                                            <td style={styles.td}>
                                                                <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', backgroundColor: '#dbeafe', color: '#1d4ed8' }}>
                                                                    Attendance
                                                                </span>
                                                            </td>
                                                            <td style={styles.td}>{r.driver?.driverName || '-'}</td>
                                                            <td style={styles.td}>{r.vehiclePlateNumber || '-'}</td>
                                                            <td style={styles.td}>
                                                                {formatTime(r.checkInTime)} - {formatTime(r.checkOutTime)}
                                                                {r.kmReading != null && ` | ${r.kmReading.toFixed(1)} km`}
                                                            </td>
                                                            <td style={styles.td}>{formatTime(r.syncedAt)}</td>
                                                        </tr>
                                                    ))}
                                                    {kmReadings.map((r, idx) => (
                                                        <tr key={`k-${idx}`} style={(attendanceRecords.length + idx) % 2 === 0 ? styles.trEven : {}}>
                                                            <td style={styles.td}>
                                                                <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', backgroundColor: '#fef3c7', color: '#92400e' }}>
                                                                    KM Reading
                                                                </span>
                                                            </td>
                                                            <td style={styles.td}>{r.driver?.driverName || '-'}</td>
                                                            <td style={styles.td}>{r.vehiclePlateNumber || '-'}</td>
                                                            <td style={styles.td}>
                                                                <span style={{ fontWeight: '600' }}>{r.kmReading != null ? r.kmReading.toFixed(1) : '-'}</span> km
                                                            </td>
                                                            <td style={styles.td}>{formatTime(r.syncedAt)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

const styles = {
    container: { padding: '20px', maxWidth: '1100px', margin: '0 auto' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 6px 0', display: 'flex', alignItems: 'center' },
    backLink: { fontSize: '13px', color: '#1976d2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
    headerActions: { display: 'flex', alignItems: 'center', gap: '10px' },
    liveIndicator: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#dcfce7', borderRadius: '20px' },
    liveDot: { width: '8px', height: '8px', backgroundColor: '#22c55e', borderRadius: '50%', animation: 'pulse 2s infinite' },
    liveText: { color: '#166534', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' },
    datePicker: { padding: '8px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '8px', outline: 'none' },
    refreshBtn: { width: '38px', height: '38px', borderRadius: '8px', border: '1px solid #ddd', backgroundColor: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#666' },
    errorAlert: { padding: '12px 16px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', display: 'flex', alignItems: 'center' },
    loadingContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#666' },
    // Summary Cards
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' },
    summaryCard: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '20px', position: 'relative', overflow: 'hidden' },
    summaryNumber: { fontSize: '28px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
    summaryLabel: { fontSize: '13px', color: '#666' },
    summaryIcon: { position: 'absolute', right: '16px', top: '18px', fontSize: '28px', color: '#1976d2', opacity: 0.15 },
    // Tabs
    tabBar: { display: 'flex', gap: '4px', marginBottom: '16px', backgroundColor: '#f1f5f9', borderRadius: '10px', padding: '4px' },
    tabButton: { flex: 1, padding: '10px 16px', fontSize: '13px', fontWeight: '500', color: '#64748b', backgroundColor: 'transparent', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' },
    tabButtonActive: { backgroundColor: '#fff', color: '#1976d2', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontWeight: '600' },
    // Card & Table
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '24px' },
    tabTitle: { fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '16px' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#333' },
    trEven: { backgroundColor: '#f8fafc' },
    // Empty State
    emptyState: { textAlign: 'center', padding: '40px 20px' },
    // Daily Grid
    dailyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' },
    dailyItem: { padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', borderLeft: '4px solid #1976d2' },
    dailyItemLabel: { fontSize: '12px', color: '#666', marginBottom: '6px' },
    dailyItemValue: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
};

// Add animations if not already present
if (!document.getElementById('fleetp-data-styles')) {
    const styleSheet = document.createElement("style");
    styleSheet.id = 'fleetp-data-styles';
    styleSheet.textContent = `
    @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
        70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
        100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
    }
    `;
    document.head.appendChild(styleSheet);
}
